import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BadgeCheck,
  Bone,
  ClipboardCheck,
  ClipboardList,
  Eye,
  FileCheck2,
  FileSearch,
  FileText,
  FileWarning,
  FlaskConical,
  Globe2,
  ImageIcon,
  Landmark,
  Microscope,
  MonitorCog,
  Network,
  Scale,
  ShieldCheck,
  Stethoscope,
  Users,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AnchorNav from "./AnchorNav";
import djordjeImg from "@/assets/services/clinical-solutions/advisors/djordje-lazovic.jpg";

const ACCENT = "#FFC810";
const NAVY = "#002855";

const anim = (index: number) => ({
  initial: { opacity: 0, y: 16 } as const,
  whileInView: { opacity: 1, y: 0 } as const,
  viewport: { once: true } as const,
  transition: { duration: 0.4, delay: index * 0.06 },
});

const SectionHeading = ({ eyebrow, title, intro }: { eyebrow: string; title: string; intro?: string }) => (
  <div className="mb-10 sm:mb-14">
    <div className="flex items-center gap-3 mb-3">
      <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: ACCENT }} />
      <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: NAVY }}>{eyebrow}</p>
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold" style={{ color: NAVY }}>{title}</h2>
    {intro && <p className="text-sm text-muted-foreground leading-relaxed mt-4 max-w-3xl">{intro}</p>}
  </div>
);

const FeatureCard = ({ icon: Icon, title, description, index }: { icon: LucideIcon; title: string; description: string; index: number }) => (
  <motion.article className="rounded-xl border border-border/40 bg-background p-5 shadow-sm" {...anim(index)}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={18} strokeWidth={1.75} style={{ color: NAVY }} />
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
    <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
  </motion.article>
);

const CaseCard = ({ title, rows, index }: { title: string; rows: { label: string; value: string }[]; index: number }) => (
  <motion.article className="rounded-xl overflow-hidden border border-border/40 bg-background shadow-sm" {...anim(index)}>
    <div className="px-5 py-3" style={{ backgroundColor: NAVY }}>
      <h3 className="text-sm font-semibold text-white">{title}</h3>
    </div>
    <dl className="p-5 space-y-3">
      {rows.map((row) => (
        <div key={row.label}>
          <dt className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: ACCENT }}>{row.label}</dt>
          <dd className="text-xs text-foreground leading-snug mt-0.5">{row.value}</dd>
        </div>
      ))}
    </dl>
  </motion.article>
);

const ChallengeCard = ({ icon: Icon, title, challenge, solution, outcome, index }: { icon: LucideIcon; title: string; challenge: string; solution: string; outcome: string; index: number }) => (
  <motion.article className="rounded-xl border border-border/40 bg-background p-5 shadow-md" {...anim(index)}>
    <div className="flex items-center gap-2 mb-4">
      <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${ACCENT}26` }}>
        <Icon size={17} strokeWidth={1.75} style={{ color: NAVY }} />
      </span>
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
    </div>
    <div className="space-y-3 text-xs leading-relaxed">
      <p><strong className="font-semibold" style={{ color: NAVY }}>Challenge: </strong><span className="text-muted-foreground">{challenge}</span></p>
      <p><strong className="font-semibold" style={{ color: NAVY }}>QbD Group Solution: </strong><span className="text-muted-foreground">{solution}</span></p>
      <div className="rounded-lg p-3 mt-2" style={{ backgroundColor: `${ACCENT}14`, borderLeft: `3px solid ${ACCENT}` }}>
        <p><strong className="font-semibold" style={{ color: NAVY }}>Outcome: </strong><span className="text-foreground font-medium">{outcome}</span></p>
      </div>
    </div>
  </motion.article>
);

const OrthopaedicSpineCROContent = ({ offeringsSlot: _offeringsSlot }: { onCtaClick: () => void; offeringsSlot?: React.ReactNode }) => {
  const sections = [
    { id: "ortho-overview", label: "Overview" },
    { id: "ortho-cases", label: "Client Cases" },
    { id: "ortho-advisory", label: "Medical Advisory Board" },
    { id: "ortho-workstreams", label: "Workstreams" },
    { id: "ortho-faq", label: "FAQ" },
  ];

  const specialisation = [
    [Bone, "Surgeon & Site Strategy", "Investigator fit, surgical volume and standardised technique to protect data quality."],
    [Users, "Recruitment & Patient Pathways", "Realistic feasibility across indications, eligibility and referral pathways."],
    [ImageIcon, "Imaging & Radiographic Endpoints", "Radiographic assessment, core-lab alignment and consistent measurement."],
    [ClipboardCheck, "PROMs & Functional Outcomes", "Validated PROMs and clinically meaningful functional endpoints."],
    [Activity, "Rehabilitation Considerations", "Rehabilitation pathways built into protocol design and follow-up windows."],
    [ShieldCheck, "Implant Survivorship & Revisions", "Long-term implant survivorship, revision tracking and PMCF follow-up."],
  ] as const;

  const medtechPackage = [
    [Stethoscope, "Clinical CRO", "Study design, operations, monitoring, PROMs, imaging and CSR."],
    [Scale, "Regulatory Affairs", "EU MDR strategy, Notified Body coordination, CER and PMCF."],
    [BadgeCheck, "Quality Assurance", "QMS, ISO 13485 and audit-ready documentation."],
    [Microscope, "Biocompatibility", "ISO 10993 biological evaluation for implant materials."],
    [MonitorCog, "Software & SaMD", "IEC 62304, validation and eIFU for connected and navigation technology."],
  ] as const;

  const cases = [
    { title: "Ortho | First-In-Human", rows: [{ label: "Indication", value: "High-risk implantable orthopaedic device" }, { label: "Study Type", value: "First-in-human / revised protocol" }, { label: "Regulatory", value: "FAGG Belgium, DSMB established" }, { label: "Services", value: "Combined RA roadmap for implant and software on one timeline, hands-on RA support" }] },
    { title: "Ortho | Hip Implant + Software", rows: [{ label: "Indication", value: "Hip implant with companion software" }, { label: "Study Type", value: "MDR transition / combined EU roadmap" }, { label: "Markets", value: "European Union" }, { label: "Services", value: "EU roadmap grouping the kit into manageable device families rather than item by item" }] },
    { title: "Surgical | Instruments", rows: [{ label: "Indication", value: "Surgical kits: drill sleeves, guides, tools" }, { label: "Study Type", value: "MDR classification & roadmap" }, { label: "Markets", value: "European Union" }, { label: "Services", value: "Sequenced four-region roadmap reusing evidence across markets" }] },
    { title: "Surgical | Drill Chucks", rows: [{ label: "Indication", value: "Surgical drill chucks" }, { label: "Study Type", value: "Multi-region regulatory roadmap" }, { label: "Markets", value: "EU, US, Canada, Australia" }, { label: "Services", value: "Protocol review, clinical development plan, medical expert workshop, submission" }] },
  ];

  const challenges = [
    { icon: ClipboardList, title: "Study Design & DSMB", challenge: "FAGG Belgium questioned a first-in-human study design over potential bias and requested a Data Safety Monitoring Board.", solution: "A protocol review workshop with our in-house medical expert and two clinical experts built a revised development plan and set up the DSMB.", outcome: "Resubmitted and accepted without further questions, with minimal timeline impact." },
    { icon: FileWarning, title: "Incomplete Submission", challenge: "A submission was rejected over a missing insurance certificate referencing the Belgian medical device law of December 2024.", solution: "A gap assessment and fully compliant resubmission within one week, refining exclusion criteria and informed consent.", outcome: "Conditional approval in March 2024, with all conditions cleared by June 2024." },
    { icon: FileText, title: "Technical Documentation", challenge: "Authorities requested additional risk analyses, biological evaluations and design verification beyond the concise initial dossier.", solution: "All requested documentation was provided promptly during the RFI phase, with the staged submission strategy adjusted to authority expectations.", outcome: "Accepted with no further requests and no timeline delays." },
  ];

  const workstreams = [
    [Globe2, "Feasibility, Surgeon & Site Strategy"],
    [FileSearch, "Protocol, Endpoints & Clinical Strategy"],
    [Landmark, "Regulatory Submissions & Safety Oversight"],
    [ShieldCheck, "Clinical Safety Management"],
    [Network, "Monitoring & Clinical Trial Operations"],
    [ImageIcon, "Imaging, PROMs & Data Management"],
    [FlaskConical, "Biostatistics, Analysis & Medical Writing"],
    [FileCheck2, "CER, PMCF & Post-Market Evidence"],
  ] as const;

  const faqs = [
    ["What Is an Orthopaedic Medical Device CRO?", "A contract research organisation that plans and runs clinical investigations for orthopaedic and spine devices, generating the clinical evidence required for EU MDR and ISO 14155 compliance."],
    ["Which Orthopaedic Devices Does QbD Group Support?", "Joint prostheses, endoprostheses, spinal systems, surgical instruments and navigation-enabled or software-driven technologies, across pre-CE and post-market studies."],
    ["What Is PMCF for Orthopaedic Implants?", "Post-market clinical follow-up is the ongoing collection of clinical data after CE marking, tracking implant survivorship, revisions, and long-term safety and performance under EU MDR."],
    ["How Do Orthopaedic Trials Differ From Other Device Studies?", "They depend heavily on surgeon and site selection, standardised surgical technique, imaging and radiographic endpoints, PROMs, rehabilitation pathways and long-term follow-up."],
    ["Can QbD Group Support Both Clinical and Regulatory Work?", "Yes. QbD Group combines clinical CRO services with regulatory affairs, quality assurance, biocompatibility and software/SaMD expertise, all under one roof."],
    ["Which European Markets Does QbD Group Cover?", "Europe is standardised under EU MDR, with distinctions for the EEA, the UK and Switzerland. Our team has direct experience with EU MDR Notified Bodies, the MHRA, Swissmedic and the FDA, and we cover the rest of the world through trusted partners and vendors."],
  ];

  return (
    <>
      <AnchorNav sections={sections} />

      <section id="ortho-overview" className="py-16 sm:py-24 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
          <SectionHeading eyebrow="Surgical endpoints" title="A CRO Dedicated to Orthopaedic Devices" />
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
              <p>Orthopaedic and spine studies demand more than standard CRO delivery. Surgeon and site selection, standardised surgical technique, imaging and radiographic endpoints, PROMs, rehabilitation pathways and long-term follow-up all influence data quality, timelines and cost.</p>
              <p>QbD Clinical is a European medical device CRO that combines clinical operations with regulatory and scientific expertise to design pragmatic evidence strategies and deliver them across Europe. We support joint prostheses, endoprostheses, spinal systems, surgical instruments and navigation-enabled technologies, from pre-CE investigations through post-market clinical follow-up (PMCF).</p>
              <p>Our objective is to generate the evidence required for EU MDR compliance, market access and your next development milestone, through efficient, fit-for-purpose study designs.</p>
            </div>

            <div className="rounded-xl p-6 sm:p-8" style={{ backgroundColor: NAVY }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-6" style={{ color: ACCENT }}>Orthopaedic & Spine Track Record</p>
              <div className="grid grid-cols-2 gap-6">
                {["25|Studies Executed", "46+|Sites Initiated", "1,432|Patients Recruited", "9|European Countries"].map((stat) => {
                  const [value, label] = stat.split("|");
                  return <div key={label}><p className="text-3xl font-bold text-white">{value}</p><p className="text-xs text-white/70 mt-1">{label}</p></div>;
                })}
              </div>
              <p className="text-xs text-white/60 leading-relaxed mt-7 pt-5 border-t border-white/15">Pre-CE and post-CE experience across Germany, Belgium, the Netherlands, Switzerland, Austria, Italy, Spain, the UK and Ireland.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" style={{ backgroundColor: "#F6F8FB" }}>
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <SectionHeading eyebrow="Surgical protocol details" title="The Realities We Design For in Orthopaedic Studies" intro="Orthopaedic and spine trials succeed or fail on details a generalist CRO can miss. We plan for them from day one." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{specialisation.map(([icon, title, description], index) => <FeatureCard key={title} icon={icon} title={title} description={description} index={index} />)}</div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <SectionHeading eyebrow="Connected device disciplines" title="The Full MedTech Evidence Package, Under One Roof" intro="As orthopaedics adopts navigation, robotics, software and connected technologies, evidence needs to span disciplines. QbD Group brings them together, so you're not stitching together vendors across the lifecycle." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">{medtechPackage.map(([icon, title, description], index) => <FeatureCard key={title} icon={icon} title={title} description={description} index={index} />)}</div>
          <div className="mt-12 border-l-4 pl-5 py-3" style={{ borderColor: ACCENT }}><p className="text-sm text-foreground leading-relaxed"><strong>From eIFU to full clinical partner.</strong> Many orthopaedic manufacturers start with us on a single service and grow into full clinical and regulatory programs as their device and evidence needs mature.</p></div>
        </div>
      </section>

      <section id="ortho-cases" className="py-20 sm:py-28 scroll-mt-28" style={{ backgroundColor: "#F6F8FB" }}>
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <SectionHeading eyebrow="Implants and surgical guidance" title="Orthopaedic & Spine Client Cases" intro="Selected engagements showing how we combine clinical, regulatory and software expertise. Anonymised." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{cases.map((item, index) => <CaseCard key={item.title} {...item} index={index} />)}</div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <SectionHeading eyebrow="Authority Response Experience" title="Regulatory Challenges, Resolved" intro="Where authority questions arose, our experience turned them into fast, compliant outcomes." />
          <div className="grid lg:grid-cols-3 gap-6">{challenges.map((item, index) => <ChallengeCard key={item.title} {...item} index={index} />)}</div>
        </div>
      </section>

      <section id="ortho-advisory" className="py-20 sm:py-28 scroll-mt-28" style={{ backgroundColor: "#F6F8FB" }}>
        <div className="container mx-auto px-6 lg:px-10 max-w-7xl">
          <SectionHeading eyebrow="Surgeon-led scientific counsel" title="Orthopaedic Expertise Built Into Your Study Strategy" intro="Direct access to an experienced orthopaedic surgeon who can challenge protocol design, endpoints, country and site selection, and your overall clinical development strategy." />
          <div className="grid lg:grid-cols-2 gap-6 items-stretch">
            <motion.article className="rounded-xl border border-border/40 bg-background p-6 shadow-sm h-full" {...anim(0)}>
              <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/40">
                <img src={djordjeImg} alt="Prof. Dr. Djordje Lazovic" className="w-20 h-20 rounded-full object-cover shrink-0" loading="lazy" />
                <div><h3 className="text-base font-semibold" style={{ color: NAVY }}>Prof. Dr. Djordje Lazovic</h3><p className="text-xs font-medium mt-1" style={{ color: ACCENT }}>Orthopaedic Surgeon & Professor</p></div>
              </div>
              <ul className="text-xs text-muted-foreground space-y-2">
                {["Orthopaedic surgeon and professor at the European Medical School Oldenburg-Groningen", "Specialised in knee surgery, endoprosthesis and surgical navigation", "Recognised expert in orthopaedics and biomechanics", "Advises sponsors on pivotal trial design, endpoints and strategic country selection"].map((item) => <li key={item} className="flex gap-2"><span className="mt-1.5 w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: ACCENT }} /><span>{item}</span></li>)}
              </ul>
            </motion.article>
            <div className="grid sm:grid-cols-2 gap-5 h-full">
              {([
                [ClipboardCheck, "Protocol & Endpoints Strategy", "Clinical input to make study design and endpoints relevant, pragmatic and executable."] as const,
                [Eye, "Independent Clinical Oversight", "Input on study design and protocol, independent Medical Monitor, CEC / DSMB member."] as const,
                [Scale, "Evidence & Risk Assessment", "Development planning, risk assessment, validating clinical relevance."] as const,
                [Globe2, "Country & Site Strategy", "Clinical perspective on country selection, investigator fit and operational feasibility."] as const,
              ] as const).map(([Icon, title, description], index) => (
                <motion.article key={title} className="rounded-xl border border-border/40 bg-background p-5 shadow-sm flex flex-col" {...anim(index)}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={17} strokeWidth={1.75} style={{ color: NAVY }} />
                    <h3 className="text-xs font-semibold text-foreground leading-snug">{title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="ortho-workstreams" className="py-20 sm:py-28 scroll-mt-28">
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <SectionHeading eyebrow="End-to-End Delivery" title="Orthopaedic Study Workstreams" intro="Build the right evidence package from study design to PMCF, with QbD Group's clinical, regulatory and wider MedTech expertise under one roof." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{workstreams.map(([Icon, label], index) => <motion.div key={label} className="flex items-center gap-3 border border-border/40 rounded-lg p-4 bg-background" {...anim(index)}><span className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${ACCENT}26` }}><Icon size={17} style={{ color: NAVY }} /></span><span className="text-xs font-semibold text-foreground leading-snug">{label}</span></motion.div>)}</div>
        </div>
      </section>

      <section id="ortho-faq" className="py-20 sm:py-28 scroll-mt-28" style={{ backgroundColor: "#F6F8FB" }}>
        <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
          <SectionHeading eyebrow="Sponsor Planning Guidance" title="Orthopaedic Device Clinical Trials: Common Questions" />
          <Accordion type="single" collapsible className="space-y-3">{faqs.map(([question, answer], index) => <AccordionItem key={question} value={`ortho-faq-${index}`} className="bg-background border border-border/40 rounded-xl px-6"><AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">{question}</AccordionTrigger><AccordionContent className="text-sm text-muted-foreground leading-relaxed">{answer}</AccordionContent></AccordionItem>)}</Accordion>
        </div>
      </section>
    </>
  );
};

export default OrthopaedicSpineCROContent;
